import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounced autosave.
 *   const { save, status, flush } = useAutoSave(dirtyValue, saveFn, { delay: 15000 });
 * `saveFn(value)` returns a promise. Fires:
 *   - after `delay` ms of no changes to `dirtyValue`
 *   - on manual `flush()`
 *   - on window unload (best-effort)
 * status: 'idle' | 'saving' | 'saved' | 'error'
 */
export function useAutoSave(value, saveFn, { delay = 15000, enabled = true } = {}) {
  const [status, setStatus] = useState("idle");
  const [lastError, setLastError] = useState(null);
  const timerRef = useRef(null);
  const lastSavedRef = useRef();
  const pendingValueRef = useRef(value);
  pendingValueRef.current = value;

  const doSave = useCallback(async (v) => {
    if (JSON.stringify(v) === JSON.stringify(lastSavedRef.current)) {
      setStatus("saved");
      return { ok: true, skipped: true };
    }
    setStatus("saving");
    setLastError(null);
    try {
      await saveFn(v);
      lastSavedRef.current = v;
      setStatus("saved");
      return { ok: true };
    } catch (err) {
      setStatus("error");
      setLastError(err?.response?.data?.detail || err?.message || "Save failed");
      return { ok: false, error: err };
    }
  }, [saveFn]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return doSave(pendingValueRef.current);
  }, [doSave]);

  useEffect(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (JSON.stringify(value) === JSON.stringify(lastSavedRef.current)) return;
    timerRef.current = setTimeout(() => { doSave(value); }, delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, delay, enabled, doSave]);

  useEffect(() => {
    const beforeUnload = () => { flush(); };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [flush]);

  // Initialise lastSaved on first mount so the first "no change" is idle, not saved.
  useEffect(() => {
    lastSavedRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, error: lastError, flush };
}
